import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  SecondaryButtonLargeV2,
} from "../Buttons/Buttons";
import { Modal } from "../Modal/Modal";
import { TextArea } from "../TextArea/TextArea";
import {
  convertUserToOption,
  useFormWrapper,
  useStoreState,
} from "../../util/util";
import type { OptionType, Permission, User } from "../../types/types";
import { TextField } from "../TextFields/TextFields";
import { SelectBoxV2 } from "../SelectBoxV2/SelectBoxV2";
import {
  MarginBottomH6,
  MarginBottomHeaderLeft,
} from "../../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/CreateAttribute";
import { FormItems } from "../../layout/FormLayout";
import { AccordionCard } from "../AccordionCard/AccordionCard";
import { Table } from "../Table/Table";
import { shouldShowObjectReferenceAttribute } from "../ObjectReference/utils";
import {
  CollectionAttributeDisplayValue,
  getCollectionAttributeDisplayValue,
} from "../../util/collectionAttributeDisplayValue";
import {
  Assets,
  AttributeSchema,
  CollectionSchema,
  PIMProduct,
} from "../../types/types.PIM";
import { useTheme } from "styled-components";
import { TableContainer } from "../../pages/SharedPages/ProductDetailPage/Assets/util";
import { DeletableChips } from "../DeletableChips/DeletableChips";
import {
  buildCollectionTableData,
  ChangeRequestFormValues,
} from "./changerequest.util";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { endpoints } from "../../endpoints";
import { fontSizeTokens, spacingTokens } from "../../designTokens";
import { ScrollTableWrapper } from "../../pages/SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { useAuthContext } from "../Auth";
import { useRoutePath } from "../../util/Routing";

const ALLOWED_ASSET_FIELDS = new Set([
  "asset_category",
  "name",
  "language",
  "document_type",
  "expiration_date",
  "issue_date",
]);

export interface CollectionModalData {
  collection: CollectionSchema;
  entityName: string;
  rowId?: string;
}
export interface GroupModalData {
  /** Attribute options derived from the group */
  attributeOptions: OptionType<string>[];
  groupName: string;
  groupId: string;
  /** Display name for the entity owning this group */
  entityName: string;
}

export interface AssetModalData {
  attributeOptions: OptionType<string>[];
  details: Assets | null;
}

type CollectionVariantProps = {
  variant: "collection";
  collectionData: CollectionModalData;
  groupData?: never;
  assetData?: never;
};

type GroupVariantProps = {
  variant: "group";
  groupData: GroupModalData;
  collectionData?: never;
  assetData?: never;
};

type AssetVariantProps = {
  variant: "asset";
  assetData: AssetModalData;
  collectionData?: never;
  groupData?: never;
};

type VariantProps =
  | CollectionVariantProps
  | GroupVariantProps
  | AssetVariantProps;

interface SharedProps {
  open: boolean;
  assignee?: PIMProduct["product_owner"] | null;
  assigneePermissions: Permission[];
  onClose: () => void;
  submitting?: boolean;
  onSubmit: (formValues: ChangeRequestFormValues) => Promise<void>;
}

export type ChangeRequestFormModalProps = SharedProps & VariantProps;

function resolveEntityName(props: VariantProps): string {
  if (props.variant === "collection") return props.collectionData.entityName;
  if (props.variant === "group") return props.groupData.entityName;
  return "Assets";
}

function resolveSelectorValue(props: VariantProps): OptionType<string> | null {
  if (props.variant === "collection") {
    const { collection } = props.collectionData;
    return {
      label: collection.display_name ?? collection.name,
      value: collection.id,
    };
  }
  if (props.variant === "group") {
    return { label: props.groupData.groupName, value: props.groupData.groupId };
  }
  return null;
}

export function ChangeRequestFormModal(
  props: ChangeRequestFormModalProps
): JSX.Element {
  const {
    open,
    onClose,
    variant,
    assetData,
    assignee,
    collectionData,
    assigneePermissions,
    groupData,
    submitting = false,
    onSubmit,
  } = props;
  const { roleIsSellerAdmin, hasPermission, user } = useAuthContext();
  const { storePath, adminPath } = useRoutePath();
  const { t } = useTranslation();
  const theme = useTheme();
  const { tenant_id } = useStoreState();
  const {
    storefront_id,
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();

  const entityName = resolveEntityName(props);
  const selectorValue = resolveSelectorValue(props);
  const methodsOfUseForm = useFormWrapper({
    defaultValues: {
      selector: selectorValue ?? undefined,
      attributes: [] as OptionType<string>[],
      assignee: assignee
        ? {
            value: assignee.id,
            label: `${assignee.firstname} ${assignee.lastname}`,
          }
        : null,
      entity_name: entityName,
      request_details: "",
    },
  });

  const { control, handleSubmit, register, formState, errors } =
    methodsOfUseForm;

  const visibleColumns = useMemo(() => {
    if (variant !== "collection") return [];
    return (collectionData.collection.columns ?? [])
      .filter(({ is_visible }) => is_visible)
      .filter((col) =>
        shouldShowObjectReferenceAttribute({
          inputType: col.input_type,
          objectReferenceEnabled: object_reference_enabled,
        })
      );
  }, [variant, collectionData, object_reference_enabled]);

  // ── Collection: table columns ───────────────────────────────

  const tableColumns = useMemo(
    () => [
      {
        header: t("Attribute Name"),
        accessorKey: "column",
        cell: ({ getValue }: { getValue: () => AttributeSchema }) => {
          const col = getValue();
          return col.display_name ?? col.name;
        },
      },
      {
        header: t("Value"),
        accessorKey: "value",
        cell: ({
          getValue,
          row,
        }: {
          getValue: () => CollectionAttributeDisplayValue;
          row: { original: { column: AttributeSchema } };
        }) =>
          getCollectionAttributeDisplayValue(row.original.column, getValue(), {
            theme,
            t,
            user,
            storePath,
            roleIsSellerAdmin,
            hasPermission,
            adminPath,
          }),
      },
    ],
    [adminPath, hasPermission, roleIsSellerAdmin, storePath, t, theme, user]
  );

  // ── Collection: table row data ─────────────────────────────────────────

  const visibleColumnById = useMemo(
    () => new Map(visibleColumns.map((col: AttributeSchema) => [col.id, col])),
    [visibleColumns]
  );

  const tableData = useMemo(() => {
    if (variant !== "collection") return [];
    const { collection, rowId } = collectionData;
    const row = rowId
      ? collection.value_rows?.find((r) => r.row_id === rowId)
      : collection.value_rows?.[0];

    return buildCollectionTableData(row, visibleColumnById);
  }, [variant, collectionData, visibleColumnById]);

  // ── Asset: table columns ────────────────────────────────────

  const assetTableColumns = useMemo(
    () => [
      {
        header: t("Attribute"),
        accessorKey: "attribute",
      },
      {
        header: t("Value"),
        accessorKey: "value",
        cell: ({ getValue }: { getValue: () => string | null }) =>
          getValue() ?? "—",
      },
    ],
    [t]
  );

  // ── Asset: table row data ──────────────────────────────────────────────

  const assetTableData = useMemo(() => {
    if (variant !== "asset" || !assetData?.details) return [];

    return assetData.attributeOptions.reduce<
      { attribute: string; value: unknown }[]
    >((acc, { label, value }) => {
      if (!ALLOWED_ASSET_FIELDS.has(value)) return acc;
      const cellValue = (assetData.details as Record<string, unknown>)[value];
      if (cellValue != null) acc.push({ attribute: label, value: cellValue });
      return acc;
    }, []);
  }, [variant, assetData]);

  // ── Attribute options for the multi-select picker ──────────────────────
  /**
   * For collection variant: derived from visible columns.
   * For group/asset variants: passed in directly by the caller.
   */
  const attributeOptions = useMemo(() => {
    if (variant === "collection") {
      return tableData.map(({ column }) => ({
        label: column.display_name ?? column.name,
        value: column.id,
      }));
    }
    if (variant === "group") return groupData.attributeOptions;
    if (variant === "asset") return assetData.attributeOptions;
    return [];
  }, [variant, tableData, groupData, assetData]);

  return (
    <Modal overlay show={open} closeModal={onClose}>
      <MarginBottomHeaderLeft
        style={{
          fontSize: fontSizeTokens.font_size_xl,
          fontWeight: "500",
          marginTop: spacingTokens.spacing_lg,
        }}
      >
        {t("Request Changes")}
      </MarginBottomHeaderLeft>

      <FormItems>
        <TextField
          name="entity_name"
          label={t("Name")}
          theref={register()}
          formState={formState}
          errors={errors}
          type="text"
          defaultValue={entityName}
          readOnly
        />

        {selectorValue && (
          <Controller
            control={control}
            name="selector"
            defaultValue={selectorValue}
            render={({ onChange, value }) => (
              <SelectBoxV2
                placeholder={
                  variant === "collection" ? t("Collection") : t("Group")
                }
                options={[selectorValue]}
                value={value ?? null}
                onChange={onChange}
                errors={errors}
                formState={formState}
                disabled
              />
            )}
          />
        )}

        {/* Assignee */}

        <Controller
          control={control}
          name="assignee"
          defaultValue={{
            value: assignee?.id,
            label: `${assignee?.firstname} ${assignee?.lastname}`,
          }}
          rules={{
            required: true,
          }}
          render={({ onChange, value }) => (
            <SearchSelectInfiniteScroll
              onChange={onChange}
              name="assignee"
              value={value}
              isClearable
              placeholder={t("Assignee")}
              baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                storefront_id,
                tenant_id
              )}
              params={(() => {
                const params = new URLSearchParams();
                params.append("order_by", "asc");
                assigneePermissions.forEach((permission) => {
                  params.append("permissions", permission);
                });
                params.append("scope", "change_request_assignee");
                return params;
              })()}
              getOptions={(response: User[]) => {
                const options = response.reduce(
                  (acc, user) =>
                    user.is_active ? [...acc, convertUserToOption(user)] : acc,
                  [] as OptionType<string>[]
                );
                // Auto-select if there's exactly one eligible assignee
                // and the field isn't already set.
                if (!value && options.length === 1) {
                  onChange(options[0]);
                }

                return options;
              }}
              errors={errors}
              formState={formState}
            />
          )}
        />

        {/* Collection row preview */}
        {variant === "collection" && (
          <AccordionCard
            title={t("Row Details")}
            expanded={true}
            actionChildren={() => <></>}
          >
            <TableContainer>
              <ScrollTableWrapper>
                <Table
                  columns={tableColumns}
                  data={tableData}
                  isLoading={false}
                  enableDefaultSort={true}
                  error={undefined}
                  lastChildleftAlign
                  highlightLinks
                />
              </ScrollTableWrapper>
            </TableContainer>
          </AccordionCard>
        )}

        {/* Asset attribute preview */}
        {variant === "asset" && (
          <AccordionCard
            title={t("Asset Details")}
            expanded={true}
            actionChildren={() => <></>}
          >
            <TableContainer>
              <Table
                columns={assetTableColumns}
                data={assetTableData}
                isLoading={false}
                enableDefaultSort={false}
                error={undefined}
                lastChildleftAlign
                highlightLinks
              />
            </TableContainer>
          </AccordionCard>
        )}

        {/* Attribute multi-select + chips */}
        <MarginBottomH6>{t("Request Details")}</MarginBottomH6>
        <Controller
          control={control}
          name="attributes"
          render={({ onChange, value }) => (
            <>
              <SelectBoxV2
                placeholder={
                  variant === "asset" ? t("Fields") : t("Attributes")
                }
                options={attributeOptions.filter(
                  (opt) =>
                    !value?.some(
                      (s: OptionType<string>) => s.value === opt.value
                    )
                )}
                value={null}
                onChange={(selected: OptionType<string>) => {
                  if (!selected) return;
                  onChange([...(value ?? []), selected]);
                }}
                formState={formState}
                errors={errors}
              />
              <DeletableChips
                chips={(value ?? []).map((s: OptionType<string>) => ({
                  id: s.value,
                  name: s.label,
                }))}
                noneSelectedName={t("Attributes")}
                handleClick={(name) => {
                  onChange(
                    value.filter((s: OptionType<string>) => s.label !== name)
                  );
                }}
                showNoneSelectedMessage={false}
              />
            </>
          )}
        />

        <TextArea
          name="request_details"
          placeholder={t("Request details")}
          label={t("Request Details")}
          theref={register({ required: true })}
          formState={formState}
          errors={errors}
          required={true}
        />
      </FormItems>

      <ButtonContainer>
        <SecondaryButtonLargeV2
          type="button"
          onClick={onClose}
          disabled={submitting}
        >
          {t("Cancel")}
        </SecondaryButtonLargeV2>
        <PrimaryButtonLargeV2
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={submitting}
        >
          {t("Submit")}
        </PrimaryButtonLargeV2>
      </ButtonContainer>
    </Modal>
  );
}
